import { useState } from 'react';
import { addDays, todayIn } from '@njpos/core';
import { api, loadSession, usd } from '../lib/api';
import { ErrorNote, Modal, useLoad } from '../components/ui';
import LotteryAudit from '../components/LotteryAudit';

type Tab = 'overview' | 'audit' | 'packs' | 'daily' | 'issues' | 'games';
const TABS: [Tab, string][] = [['overview', 'Overview'], ['audit', 'Audit'], ['packs', 'Packs'], ['daily', 'State report'], ['issues', 'Issues'], ['games', 'Games & setup']];

export default function Lottery() {
  const [tab, setTab] = useState<Tab>('overview');
  const isManager = loadSession()?.user.role !== 'cashier';
  const visible = TABS.filter(([k]) => isManager || k === 'audit' || k === 'packs');
  return (
    <div className="stack">
      <div className="page-head"><div className="grow"><h1>Lottery</h1><p>Track scratch-off packs from the shelf to the register, check each shift against the register, and catch missing packs and cash early.</p></div></div>
      <div className="row wrap" role="tablist">
        {visible.map(([k, label]) => <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'primary' : ''} onClick={() => setTab(k)}>{label}</button>)}
      </div>
      {tab === 'overview' && <Overview />}
      {tab === 'audit' && <AuditTab />}
      {tab === 'packs' && <Packs isManager={isManager} />}
      {tab === 'daily' && <Daily />}
      {tab === 'issues' && <Issues />}
      {tab === 'games' && <Games />}
    </div>
  );
}

// ---------------- overview ----------------
function Overview() {
  const today = todayIn();
  const [from, setFrom] = useState(addDays(today, -6));
  const [to, setTo] = useState(today);
  const s = useLoad(() => api<any>(`/api/lottery/summary?from=${from}&to=${to}`), [from, to]);
  const d = s.data;
  return (
    <div className="stack">
      <div className="row wrap"><label>From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label><label>To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label></div>
      <ErrorNote error={s.error} />
      {d && (
        <>
          <div className="stats">
            <div><div className="v">{usd(d.instantSalesCents)}</div><div className="l">Instant sales (register)</div></div>
            <div><div className="v">{usd(d.onlineSalesCents)}</div><div className="l">Online sales (register)</div></div>
            <div><div className="v">{usd(d.payoutsCents)}</div><div className="l">Paid out</div></div>
            <div><div className="v">{usd(d.netCents)}</div><div className="l">Owed to the state</div></div>
          </div>
          <div className="stats">
            <div><div className="v">{usd(d.countedCents)}</div><div className="l">Packs say sold ({d.audits} audits)</div></div>
            <div><div className="v" style={{ color: d.auditVarianceCents < 0 ? 'var(--bad)' : undefined }}>{usd(d.auditVarianceCents)}</div><div className="l">Over / short from audits</div></div>
            <div><div className="v">{d.openIssues}</div><div className="l">Open issues</div></div>
            <div><div className="v">{d.packs.active ?? 0} / {d.packs.backstock ?? 0}</div><div className="l">Packs active / backstock</div></div>
          </div>
          <div className="panel"><div className="panel-head"><h2>By day (what the register recorded)</h2></div>
            <table className="grid"><thead><tr><th>Day</th><th className="n">Instant</th><th className="n">Online</th><th className="n">Payouts</th></tr></thead>
              <tbody>{[...d.days].reverse().map((x: any) => <tr key={x.day}><td>{x.day}</td><td className="n">{usd(x.instant_sales)}</td><td className="n">{usd(x.online_sales)}</td><td className="n">{usd(x.payouts)}</td></tr>)}</tbody></table>
            {d.days.length === 0 && <div className="empty">No lottery entries in this period. Record sales from the register's Lottery button.</div>}
          </div>
        </>
      )}
    </div>
  );
}

// ---------------- audit ----------------
function AuditTab() {
  const audits = useLoad(() => api<any[]>('/api/lottery/audits'), []);
  return (
    <div className="stack">
      <div className="panel"><div className="panel-body"><LotteryAudit onDone={() => audits.reload()} /></div></div>
      <div className="panel"><div className="panel-head"><h2>Past audits</h2></div>
        <table className="grid"><thead><tr><th>When</th><th>By</th><th className="n">Packs</th><th className="n">Packs say</th><th className="n">Register</th><th className="n">Over / short</th></tr></thead>
          <tbody>{audits.data?.map((a) => (
            <tr key={a.id}><td>{new Date(a.counted_at).toLocaleString()}</td><td>{a.by_name}</td><td className="n">{a.packs_scanned}</td><td className="n">{usd(a.counted_cents)}</td><td className="n">{usd(a.recorded_cents)}</td>
              <td className="n"><span className={`chip ${a.variance_cents === 0 ? 'good' : 'bad'}`}>{a.variance_cents === 0 ? 'even' : usd(a.variance_cents)}</span></td></tr>))}</tbody></table>
        {audits.data?.length === 0 && <div className="empty">No audits yet.</div>}
      </div>
    </div>
  );
}

// ---------------- packs ----------------
function Packs({ isManager }: { isManager: boolean }) {
  const [status, setStatus] = useState('active');
  const packs = useLoad(() => api<any[]>(`/api/lottery/packs?status=${status}`), [status]);
  const [receiving, setReceiving] = useState(false);
  const [act, setAct] = useState<any>(null);
  const [err, setErr] = useState<unknown>(null);
  async function run(path: string, body: unknown = {}) { setErr(null); try { await api(path, { body }); packs.reload(); } catch (e) { setErr(e); } }
  return (
    <div className="stack">
      <div className="row wrap">
        <select aria-label="Pack status" style={{ width: 'auto' }} value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="active">In use (active)</option><option value="backstock">Backstock</option><option value="settled">Finished</option><option value="missing">Missing</option><option value="returned">Returned</option>
        </select>
        <button className="primary" onClick={() => setReceiving(true)}>Receive packs</button>
      </div>
      <ErrorNote error={packs.error ?? err} />
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Game</th><th>Pack</th><th>Bin</th><th className="n">Next ticket</th><th>Since</th><th /></tr></thead>
          <tbody>{packs.data?.map((p) => (
            <tr key={p.id}>
              <td>{p.game_name} <span className="faint">${(p.ticket_price_cents / 100).toFixed(0)} · {p.game_no}</span></td><td className="num">{p.pack_no}</td><td>{p.bin ?? '—'}</td>
              <td className="n">{p.position ?? '—'}</td><td>{new Date(p.activated_at ?? p.received_at).toLocaleDateString()}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {p.status === 'backstock' && <button onClick={() => setAct(p)}>Activate</button>}{' '}
                {p.status === 'active' && isManager && <button onClick={() => { if (confirm(`Finish ${p.game_name} pack ${p.pack_no}? The tickets left are counted as sold at the next audit.`)) run(`/api/lottery/packs/${p.id}/settle`); }}>Sold out</button>}{' '}
                {isManager && (p.status === 'backstock' || p.status === 'active') && <button onClick={() => { const note = prompt('Missing: add a note (optional)'); if (note !== null) run(`/api/lottery/packs/${p.id}/missing`, { note }); }}>Missing</button>}{' '}
                {isManager && p.status === 'backstock' && <button onClick={() => { if (confirm('Return this pack to the state?')) run(`/api/lottery/packs/${p.id}/return`); }}>Return</button>}
              </td>
            </tr>))}</tbody>
        </table>
        {packs.data?.length === 0 && <div className="empty">No packs here.</div>}
      </div></div>
      {receiving && <Receive onClose={() => { setReceiving(false); packs.reload(); }} />}
      {act && <Activate pack={act} onClose={() => setAct(null)} onDone={() => { setAct(null); packs.reload(); }} />}
    </div>
  );
}

function Receive({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [err, setErr] = useState<unknown>(null);
  const [adding, setAdding] = useState<any>(null);
  async function scan(code: string) {
    setErr(null);
    try { const r = await api<any[]>('/api/lottery/packs/receive', { body: { codes: [code] } }); setRows((x) => [...r, ...x]); } catch (e) { setErr(e); }
  }
  return (
    <Modal title="Receive packs" onClose={onClose} wide footer={<button className="primary" onClick={onClose}>Done</button>}>
      <p className="muted">Scan each pack as it comes off the delivery. It goes into backstock until you activate it.</p>
      <form className="scanbar" onSubmit={(e) => { e.preventDefault(); const c = text.trim(); setText(''); if (c) scan(c); }}>
        <input autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder="Scan the pack barcode" inputMode="numeric" aria-label="Pack barcode" />
        <button className="primary big">Add</button>
      </form>
      <ErrorNote error={err} />
      {rows.map((r, i) => (
        <div key={i} className="m-line">
          <span className={`chip ${r.result === 'received' ? 'good' : 'warn'}`}>{r.result.replace('_', ' ')}</span>
          <div className="grow">{r.game ?? (r.gameNo ? `Game ${r.gameNo}` : r.code)}{r.packNo ? ` · pack ${r.packNo}` : ''}</div>
          {r.result === 'unknown_game' && <button onClick={() => setAdding(r)}>Add game {r.gameNo}</button>}
        </div>))}
      {adding && <GameForm initial={{ gameNo: adding.gameNo }} onClose={() => setAdding(null)} onDone={async () => { setAdding(null); await scan(adding.code); }} />}
    </Modal>
  );
}

function Activate({ pack, onClose, onDone }: { pack: any; onClose: () => void; onDone: () => void }) {
  const [bin, setBin] = useState('');
  const [start, setStart] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={`Activate ${pack.game_name} pack ${pack.pack_no}`} onClose={onClose} footer={<button className="primary" onClick={async () => {
      try { await api(`/api/lottery/packs/${pack.id}/activate`, { body: { bin: bin || null, ...(start !== '' ? { startTicket: Number(start) } : {}) } }); onDone(); } catch (e) { setErr(e); }
    }}>Activate</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Bin or slot<input autoFocus value={bin} onChange={(e) => setBin(e.target.value)} placeholder="e.g. 12" /></label>
        <label>First ticket (leave blank for the start of the pack)<input inputMode="numeric" value={start} onChange={(e) => setStart(e.target.value.replace(/\D/g, ''))} placeholder={pack.order_dir === 'asc' ? '0' : String(pack.tickets_per_pack - 1)} /></label>
      </div>
      <p className="faint">The pack is counted from this ticket. Tickets already torn off before you activate it are not counted as sales.</p>
    </Modal>
  );
}

// ---------------- the state's daily report ----------------
function Daily() {
  const today = todayIn();
  const from = addDays(today, -13);
  const days = useLoad(() => api<any[]>(`/api/lottery/daily?from=${from}&to=${today}`), []);
  const settlements = useLoad(() => api<any[]>('/api/lottery/settlements'), []);
  const [edit, setEdit] = useState<{ day: string; state: any } | null>(null);
  const [settling, setSettling] = useState(false);
  const diff = (n: number | undefined) => (n === undefined ? '' : n === 0 ? <span className="chip good">match</span> : <span className="chip bad">{n > 0 ? '+' : '−'}{usd(Math.abs(n))}</span>);
  return (
    <div className="stack">
      <p className="muted">Type in the totals from the state lottery terminal's day report. The POS compares them with what your register recorded. A difference opens an issue.</p>
      <div className="panel"><div className="table-wrap">
        <table className="grid">
          <thead><tr><th>Day</th><th className="n">Instant (register)</th><th className="n">Online (register)</th><th className="n">Paid out (register)</th><th>Vs state report</th><th /></tr></thead>
          <tbody>{days.data?.map((d) => (
            <tr key={d.day}><td>{d.day}</td><td className="n">{usd(d.register.instant)}</td><td className="n">{usd(d.register.online)}</td><td className="n">{usd(d.register.payouts)}</td>
              <td>{d.state ? <>{diff(d.diffs.instant)} {diff(d.diffs.online)} {diff(d.diffs.payouts)}</> : <span className="faint">not entered</span>}</td>
              <td><button onClick={() => setEdit({ day: d.day, state: d.state })}>{d.state ? 'Edit' : 'Enter report'}</button></td></tr>))}</tbody>
        </table>
        {days.data?.length === 0 && <div className="empty">No lottery activity in the last two weeks.</div>}
      </div></div>
      <div className="panel"><div className="panel-head"><h2 className="grow">Settlement with the state</h2><button onClick={() => setSettling(true)}>Record a settlement</button></div>
        <table className="grid"><thead><tr><th>Period</th><th className="n">Sales</th><th className="n">Payouts</th><th className="n">State debited</th><th className="n">Your commission</th></tr></thead>
          <tbody>{settlements.data?.map((s) => <tr key={s.id}><td>{s.period_from} to {s.period_to}</td><td className="n">{usd(s.sales_cents)}</td><td className="n">{usd(s.payouts_cents)}</td><td className="n">{usd(s.state_debit_cents)}</td><td className="n">{usd(s.commission_cents)}</td></tr>)}</tbody></table>
        {settlements.data?.length === 0 && <div className="empty">No settlements recorded.</div>}
      </div>
      {edit && <EnterReport day={edit.day} state={edit.state} onClose={() => setEdit(null)} onDone={() => { setEdit(null); days.reload(); }} />}
      {settling && <Settle onClose={() => setSettling(false)} onDone={() => { setSettling(false); settlements.reload(); }} />}
    </div>
  );
}

const centsOf = (v: string) => Math.round(Number(v || '0') * 100);
const money = (v: string) => v.replace(/[^\d.]/g, '');

function EnterReport({ day, state, onClose, onDone }: { day: string; state: any; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ instant: state ? (state.instant / 100).toFixed(2) : '', online: state ? (state.online / 100).toFixed(2) : '', payouts: state ? (state.payouts / 100).toFixed(2) : '' });
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title={`State report for ${day}`} onClose={onClose} footer={<button className="primary" onClick={async () => {
      try { await api(`/api/lottery/daily/${day}`, { method: 'PUT', body: { instantSalesCents: centsOf(f.instant), onlineSalesCents: centsOf(f.online), payoutsCents: centsOf(f.payouts) } }); onDone(); } catch (e) { setErr(e); }
    }}>Save and compare</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Instant sales<input autoFocus className="num-input" inputMode="decimal" value={f.instant} onChange={(e) => setF({ ...f, instant: money(e.target.value) })} placeholder="0.00" /></label>
        <label>Online sales<input className="num-input" inputMode="decimal" value={f.online} onChange={(e) => setF({ ...f, online: money(e.target.value) })} placeholder="0.00" /></label>
        <label>Cashed (payouts)<input className="num-input" inputMode="decimal" value={f.payouts} onChange={(e) => setF({ ...f, payouts: money(e.target.value) })} placeholder="0.00" /></label>
      </div>
    </Modal>
  );
}

function Settle({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const today = todayIn();
  const [from, setFrom] = useState(addDays(today, -6));
  const [to, setTo] = useState(today);
  const [debit, setDebit] = useState('');
  const [err, setErr] = useState<unknown>(null);
  return (
    <Modal title="Record a settlement" onClose={onClose} footer={<button className="primary" disabled={!debit} onClick={async () => {
      try { await api('/api/lottery/settlements', { body: { from, to, stateDebitCents: centsOf(debit) } }); onDone(); } catch (e) { setErr(e); }
    }}>Record</button>}>
      <ErrorNote error={err} />
      <p className="muted">Enter the amount the state took from your bank account for this period. The difference between sales minus payouts and that amount is your commission.</p>
      <div className="field-row">
        <label>From<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>To<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <label>State debited<input className="num-input" inputMode="decimal" value={debit} onChange={(e) => setDebit(money(e.target.value))} placeholder="0.00" /></label>
      </div>
    </Modal>
  );
}

// ---------------- issues ----------------
function Issues() {
  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const issues = useLoad(() => api<any[]>(`/api/lottery/issues?status=${status}`), [status]);
  const [err, setErr] = useState<unknown>(null);
  return (
    <div className="stack">
      <div className="row wrap">
        <button className={status === 'open' ? 'primary' : ''} onClick={() => setStatus('open')}>Open</button>
        <button className={status === 'resolved' ? 'primary' : ''} onClick={() => setStatus('resolved')}>Resolved</button>
      </div>
      <ErrorNote error={issues.error ?? err} />
      <div className="panel">
        {issues.data?.map((i) => (
          <div key={i.id} className="m-line">
            <span className={`chip ${i.severity === 'critical' ? 'bad' : 'warn'}`}>{i.kind.replace('_', ' ')}</span>
            <div className="grow"><div>{i.message}</div><div className="faint">{new Date(i.created_at).toLocaleString()}{i.user_names ? ` · on the register: ${i.user_names}` : ''}{i.resolve_note ? ` · ${i.resolve_note}` : ''}</div></div>
            {status === 'open' && <button onClick={async () => { const note = prompt('How was this resolved?'); if (!note) return; setErr(null); try { await api(`/api/lottery/issues/${i.id}/resolve`, { body: { note } }); issues.reload(); } catch (e) { setErr(e); } }}>Resolve</button>}
          </div>))}
        {issues.data?.length === 0 && <div className="empty">{status === 'open' ? 'No open issues.' : 'Nothing resolved yet.'}</div>}
      </div>
    </div>
  );
}

// ---------------- games and barcode setup ----------------
function Games() {
  const games = useLoad(() => api<any[]>('/api/lottery/games'), []);
  const settings = useLoad(() => api<any>('/api/lottery/settings'), []);
  const [form, setForm] = useState<any>(null);
  const [layout, setLayout] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState<unknown>(null);
  const l = layout ?? settings.data?.layout;
  return (
    <div className="stack">
      <div className="panel"><div className="panel-head"><h2 className="grow">Scratch-off games</h2><button className="primary" onClick={() => setForm({})}>Add game</button></div>
        <table className="grid"><thead><tr><th>Game</th><th>Name</th><th className="n">Ticket</th><th className="n">Per pack</th><th>Sold</th><th /></tr></thead>
          <tbody>{games.data?.map((g) => (
            <tr key={g.id}><td className="num">{g.game_no}</td><td>{g.name}</td><td className="n">{usd(g.ticket_price_cents)}</td><td className="n">{g.tickets_per_pack}</td><td>{g.order_dir === 'asc' ? 'from 0 up' : 'from last ticket down'}</td>
              <td><button onClick={() => setForm({ gameNo: g.game_no, name: g.name, price: (g.ticket_price_cents / 100).toString(), perPack: String(g.tickets_per_pack), dir: g.order_dir })}>Edit</button></td></tr>))}</tbody></table>
        {games.data?.length === 0 && <div className="empty">No games yet. Add one when you receive its first pack.</div>}
      </div>
      <div className="panel"><div className="panel-head"><h2>Ticket barcode</h2></div><div className="panel-body stack">
        <p className="muted">How the digits on a ticket split into game, pack and ticket number. Check one of your tickets and adjust if the preview looks wrong.</p>
        <ErrorNote error={err ?? settings.error} />
        {msg && <div className="notice good">{msg}</div>}
        {l && (
          <div className="field-row">
            <label>Game digits<input inputMode="numeric" value={l.gameDigits} onChange={(e) => setLayout({ ...l, gameDigits: Number(e.target.value) || 0 })} /></label>
            <label>Pack digits<input inputMode="numeric" value={l.packDigits} onChange={(e) => setLayout({ ...l, packDigits: Number(e.target.value) || 0 })} /></label>
            <label>Ticket digits<input inputMode="numeric" value={l.ticketDigits} onChange={(e) => setLayout({ ...l, ticketDigits: Number(e.target.value) || 0 })} /></label>
            <button disabled={!layout} onClick={async () => { setErr(null); setMsg(''); try { await api('/api/lottery/settings', { method: 'PUT', body: { layout } }); setLayout(null); settings.reload(); setMsg('Saved.'); } catch (e) { setErr(e); } }}>Save</button>
          </div>
        )}
      </div></div>
      {form && <GameForm initial={form} onClose={() => setForm(null)} onDone={() => { setForm(null); games.reload(); }} />}
    </div>
  );
}

function GameForm({ initial, onClose, onDone }: { initial: any; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({ gameNo: initial.gameNo ?? '', name: initial.name ?? '', price: initial.price ?? '', perPack: initial.perPack ?? '', dir: initial.dir ?? 'asc' });
  const [err, setErr] = useState<unknown>(null);
  const ok = f.gameNo && f.name && Number(f.price) > 0 && Number(f.perPack) > 0;
  return (
    <Modal title={initial.name ? 'Edit game' : 'Add a scratch-off game'} onClose={onClose} footer={<button className="primary" disabled={!ok} onClick={async () => {
      try { await api('/api/lottery/games', { body: { gameNo: f.gameNo, name: f.name, ticketPriceCents: Math.round(Number(f.price) * 100), ticketsPerPack: Number(f.perPack), orderDir: f.dir } }); onDone(); } catch (e) { setErr(e); }
    }}>Save</button>}>
      <ErrorNote error={err} />
      <div className="field-row">
        <label>Game number<input autoFocus value={f.gameNo} onChange={(e) => setF({ ...f, gameNo: e.target.value })} /></label>
        <label>Name<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Cash Blast" /></label>
      </div>
      <div className="field-row">
        <label>Ticket price ($)<input className="num-input" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: money(e.target.value) })} /></label>
        <label>Tickets in a pack<input inputMode="numeric" value={f.perPack} onChange={(e) => setF({ ...f, perPack: e.target.value.replace(/\D/g, '') })} /></label>
        <label>Tickets are sold<select value={f.dir} onChange={(e) => setF({ ...f, dir: e.target.value })}><option value="asc">From ticket 0 upward</option><option value="desc">From the last ticket downward</option></select></label>
      </div>
      <p className="faint">The price and pack size are printed on the game's flyer and the pack. The direction is how your clerks tear tickets off: check one pack to be sure.</p>
    </Modal>
  );
}
